import { useState, useEffect } from "react";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { QRCodeCanvas } from "qrcode.react";

// Sử dụng URL công khai cho logo
const stripeLogo = "https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.png";
const paypalLogo = "https://upload.wikimedia.org/wikipedia/commons/a/a4/Paypal_2014_logo.png";
const coinpaymentsLogo = "https://www.coinpayments.net/images/logo.png";
const coinbaseLogo = "https://commerce.coinbase.com/favicon.ico";
const nowpaymentsLogo = "https://nowpayments.io/images/logo/logo.svg";
const binancepayLogo = "https://www.binance.com/resources/img/logo/binance-pay-logo.png";
const momoLogo = "https://www.momo.vn/static/img/logo-momo.png";
const zalopayLogo = "https://gateway.zalopay.vn/static/zalopay-logo.png";
const vnpayLogo = "https://vnpay.vn/assets/images/logo-icon/logo-primary-1.png";
const vietqrLogo = "https://upload.wikimedia.org/wikipedia/commons/5/5e/Logo_VietQR.png";

// Component xử lý thanh toán Stripe
// Component xử lý thanh toán Stripe
// Component xử lý thanh toán Stripe
// Component xử lý thanh toán Stripe
const StripeCheckoutForm = ({ plan, onSuccess, isCoinPurchase = false, packageId, fetchWithRetry }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [postalCode, setPostalCode] = useState("");

  const handleStripeSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setProcessing(true);

    if (!postalCode || postalCode.length < 5) {
      setError("Vui lòng nhập mã bưu điện hợp lệ (ít nhất 5 chữ số).");
      setProcessing(false);
      return;
    }

    try {
      const amount = isCoinPurchase ? plan.price : plan.totalPrice;
      if (!amount || isNaN(amount)) {
        throw new Error("Số tiền không hợp lệ! Vui lòng kiểm tra gói.");
      }

      const paymentData = {
        amount,
        ...(isCoinPurchase ? { packageId: plan._id } : { planId: plan._id }),
      };

      const response = await fetchWithRetry({
        method: "post",
        url: isCoinPurchase ? "/api/payments/create-coin-payment-intent" : "/api/payments/create-membership-payment-intent",
        data: paymentData,
      });

      const { clientSecret } = response.data;
      if (!clientSecret) {
        throw new Error("Không nhận được clientSecret từ server!");
      }

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            address: {
              postal_code: postalCode,
            },
          },
        },
      });

      if (result.error) {
        setError(result.error.message);
        setProcessing(false);
      } else if (result.paymentIntent.status === "requires_action") {
        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret);
        if (error) {
          setError(error.message);
          setProcessing(false);
        } else if (paymentIntent.status === "succeeded") {
          await completePayment(paymentIntent.id);
        }
      } else if (result.paymentIntent.status === "succeeded") {
        await completePayment(result.paymentIntent.id); // Sửa lỗi: Sử dụng result.paymentIntent thay vì paymentIntent
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        (err.response?.data?.details ? `Lỗi: ${err.response.data.details}` : "Lỗi khi xử lý thanh toán Stripe! Vui lòng thử lại.")
      );
      setProcessing(false);
    }
  };

  const completePayment = async (stripePaymentIntentId) => {
    try {
      const response = await fetchWithRetry({
        method: "post",
        url: isCoinPurchase ? "/api/payments/purchase-coin" : "/api/payments/upgrade-membership",
        data: {
          [isCoinPurchase ? "packageId" : "planId"]: plan._id,
          paymentMethod: "stripe",
          stripePaymentIntentId,
        },
      });
      setError(null);
      onSuccess(response.data.message || "Thanh toán thành công!");
    } catch (err) {
      setError(
        err.response?.data?.message ||
        (err.response?.data?.details ? `Lỗi: ${err.response.data.details}` : "Lỗi khi hoàn tất thanh toán! Vui lòng kiểm tra lịch sử giao dịch hoặc thử lại.")
      );
      setProcessing(false);
    }
  };

  const cardElementOptions = {
    hidePostalCode: true,
    style: {
      base: {
        fontSize: "16px",
        color: "#32325d",
        "::placeholder": {
          color: "#aab7c4",
        },
      },
      invalid: {
        color: "#fa755a",
      },
    },
  };

  return (
    <form onSubmit={handleStripeSubmit} className="space-y-4">
      <div>
        <label className="block text-gray-700 font-medium mb-1">Thông tin thẻ</label>
        <CardElement options={cardElementOptions} className="p-3 border rounded-lg" />
      </div>
      <div>
        <label className="block text-gray-700 font-medium mb-1">Mã bưu điện</label>
        <input
          type="text"
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value)}
          placeholder="Nhập mã bưu điện (ví dụ: 12345)"
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      {error && <p className="text-red-500">{error}</p>}
      <button
        type="submit"
        disabled={!stripe || !elements || processing}
        className="w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 disabled:bg-gray-400 transition duration-200"
      >
        {processing ? "Đang xử lý..." : "Thanh toán qua Stripe"}
      </button>
    </form>
  );
};

// Component xử lý thanh toán PayPal qua API
const PaypalApiCheckoutForm = ({ plan, onSuccess, clientId, isCoinPurchase = false, packageId, fetchWithRetry }) => {
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("paypal"); // Mặc định là PayPal

  const createPaypalOrder = async () => {
    setError(null);
    setProcessing(true);

    try {
      if (!clientId) {
        throw new Error("Client ID của PayPal không hợp lệ! Vui lòng kiểm tra cấu hình.");
      }

      const amount = isCoinPurchase ? plan.price : plan.totalPrice;
      if (!amount || isNaN(amount)) {
        throw new Error("Số tiền không hợp lệ! Vui lòng kiểm tra gói.");
      }

      const paymentData = {
        amount,
        ...(isCoinPurchase ? { packageId: plan._id } : { planId: plan._id }),
      };

      const response = await fetchWithRetry({
        method: "post",
        url: isCoinPurchase ? "/api/payments/create-coin-paypal-order" : "/api/payments/create-membership-paypal-order",
        data: paymentData,
      });

      const orderId = response.data.orderId;
      if (!orderId) {
        throw new Error("Không nhận được orderId từ server!");
      }

      return orderId;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        (error.response?.data?.error ? `Lỗi: ${error.response.data.error}` : "Lỗi khi tạo PayPal Order! Vui lòng thử lại.");
      setError(errorMessage);
      setProcessing(false);
      return null;
    }
  };

  const onPaypalApprove = async (data) => {
    try {
      setProcessing(true);
      const response = await fetchWithRetry({
        method: "post",
        url: isCoinPurchase ? "/api/payments/purchase-coin" : "/api/payments/upgrade-membership",
        data: {
          [isCoinPurchase ? "packageId" : "planId"]: plan._id,
          paymentMethod: "paypal_api",
          paypalOrderId: data.orderID,
        },
      });
      setError(null);
      onSuccess(response.data.message || "Thanh toán thành công!");
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        (error.response?.data?.error ? `Lỗi: ${error.response.data.error}` : "Lỗi khi xử lý thanh toán! Vui lòng thử lại.");
      if (error.response?.data?.error?.includes("UNPROCESSABLE_ENTITY")) {
        setError("Đơn hàng không hợp lệ hoặc đã được xử lý. Vui lòng kiểm tra lịch sử giao dịch hoặc thử lại với đơn hàng mới.");
      } else {
        setError(errorMessage);
      }
      setProcessing(false);
    }
  };

  const handlePaypalError = (err) => {
    let errorMessage = `Lỗi khi thanh toán qua ${paymentMethod === "paypal" ? "PayPal" : "Thẻ tín dụng/thẻ ghi nợ"}! Vui lòng thử lại.`;
    if (err.message) {
      if (err.message.includes("NETWORK_ERROR")) {
        errorMessage = "Lỗi kết nối mạng khi thanh toán! Vui lòng kiểm tra kết nối internet hoặc thử lại.";
      } else if (err.message.includes("POPUP_BLOCKED")) {
        errorMessage = "Cửa sổ thanh toán bị chặn! Vui lòng tắt trình chặn popup trong trình duyệt và thử lại.";
      } else if (err.message.includes("UNPROCESSABLE_ENTITY")) {
        errorMessage = "Đơn hàng không hợp lệ! Vui lòng thử lại với đơn hàng mới.";
      } else if (err.message.includes("BLOCKED_BY_ADBLOCKER")) {
        errorMessage = "Thanh toán bị chặn bởi trình chặn quảng cáo (adblocker)! Vui lòng tắt adblocker hoặc thêm website vào danh sách trắng, sau đó thử lại.";
      } else if (err.message.includes("Extension context invalidated")) {
        errorMessage = "Thanh toán bị chặn bởi một tiện ích mở rộng (extension)! Vui lòng tắt các tiện ích như AdBlock, VPN, hoặc tiện ích liên quan, sau đó thử lại.";
      } else {
        errorMessage = `Lỗi không xác định: ${err.message}. Vui lòng liên hệ hỗ trợ hoặc thử lại.`;
      }
    }
    setError(errorMessage);
    setProcessing(false);
  };

  // Kiểm tra clientId trước khi render PayPalScriptProvider
  if (!clientId) {
    return (
      <div className="space-y-4">
        <p className="text-red-500">Client ID của PayPal không hợp lệ! Vui lòng kiểm tra cấu hình.</p>
      </div>
    );
  }

  return (
    <PayPalScriptProvider options={{ "client-id": clientId }}>
      <div className="space-y-4">
        {/* Giao diện lựa chọn phương thức thanh toán */}
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-2">Chọn phương thức thanh toán:</label>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="paypal"
                checked={paymentMethod === "paypal"}
                onChange={() => setPaymentMethod("paypal")}
                className="mr-2"
              />
              <span>PayPal</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="card"
                checked={paymentMethod === "card"}
                onChange={() => setPaymentMethod("card")}
                className="mr-2"
              />
              <span>Thẻ tín dụng/Thẻ ghi nợ</span>
            </label>
          </div>
        </div>

        {/* Hiển thị lỗi nếu có */}
        {error && <p className="text-red-500">{error}</p>}

        {/* Hiển thị PayPal Buttons với tùy chọn phương thức thanh toán */}
        <div className="relative">
          {processing && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50">
              <svg
                className="animate-spin h-8 w-8 text-blue-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          )}
          <PayPalButtons
            createOrder={async () => {
              const orderId = await createPaypalOrder();
              if (!orderId) {
                throw new Error("Không thể tạo PayPal Order!");
              }
              return orderId;
            }}
            onApprove={onPaypalApprove}
            onError={handlePaypalError}
            disabled={processing}
            fundingSource={paymentMethod} // Sử dụng phương thức thanh toán được chọn
            style={{
              layout: "vertical",
              color: "gold",
              shape: "rect",
              label: paymentMethod === "paypal" ? "paypal" : "pay",
            }}
          />
        </div>
      </div>
    </PayPalScriptProvider>
  );
};

// Component xử lý thanh toán PayPal qua PayPal.me
const PaypalMeCheckoutForm = ({ plan, username, onPending, isCoinPurchase = false, packageId, fetchWithRetry }) => {
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const amountInUSD = Math.max(1, Math.round((isCoinPurchase ? plan.price : plan.totalPrice) / 23000));
  const paypalMeLink = `https://paypal.me/${username}/${amountInUSD}`;

  const handlePaypalRedirect = async () => {
    setError(null);
    setProcessing(true);

    if (!username) {
      setError("Username PayPal.me không hợp lệ! Vui lòng kiểm tra cấu hình.");
      setProcessing(false);
      return;
    }

    const usernameRegex = /^[a-zA-Z0-9]+$/;
    if (!usernameRegex.test(username)) {
      setError("Username PayPal.me không đúng định dạng! Vui lòng kiểm tra lại.");
      setProcessing(false);
      return;
    }

    try {
      window.open(paypalMeLink, "_blank");
      const response = await fetchWithRetry({
        method: "post",
        url: isCoinPurchase ? "/api/payments/purchase-coin" : "/api/payments/upgrade-membership",
        data: {
          [isCoinPurchase ? "packageId" : "planId"]: plan._id,
          paymentMethod: "paypal_me",
          paypalTransactionId: `TXN-${Date.now()}`,
        },
      });
      setError(null);
      onPending(response.data.message || "Giao dịch đã được ghi nhận và đang chờ xác minh.");
      setTimeout(() => {
        setError("Vui lòng kiểm tra tab PayPal.me để hoàn tất thanh toán.");
      }, 1000);
    } catch (err) {
      setError(
        err.response?.data?.message ||
        (err.response?.data?.details ? `Lỗi khi mở liên kết PayPal.me: ${JSON.stringify(err.response.data.details)}` : "Không thể mở liên kết PayPal.me! Vui lòng thử lại.")
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && <p className="text-red-500">{error}</p>}
      <button
        onClick={handlePaypalRedirect}
        disabled={processing}
        className="w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition duration-200 disabled:bg-gray-400"
      >
        {processing ? "Đang xử lý..." : "Thanh toán qua PayPal.me"}
      </button>
    </div>
  );
};

// Component xử lý thanh toán CoinPayments
const CoinpaymentsCheckoutForm = ({ plan, merchantId, onPending, isCoinPurchase = false, packageId, fetchWithRetry }) => {
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState("BTC");

  const handleCoinpaymentsSubmit = async () => {
    setError(null);
    setProcessing(true);

    try {
      const amount = (isCoinPurchase ? plan.price : plan.totalPrice) / 23000;
      if (!amount || isNaN(amount)) {
        throw new Error("Số tiền không hợp lệ! Vui lòng kiểm tra gói.");
      }

      const paymentData = {
        amount: amount.toFixed(2),
        currency: selectedCurrency,
        ...(isCoinPurchase ? { packageId: plan._id } : { planId: plan._id }),
      };

      const response = await fetchWithRetry({
        method: "post",
        url: isCoinPurchase ? "/api/payments/create-coin-coinpayments-transaction" : "/api/payments/create-membership-coinpayments-transaction",
        data: paymentData,
      });

      const transactionData = response.data.transaction;
      setTransaction(transactionData);
      setError(null);
      onPending(response.data.message || "Giao dịch đã được tạo. Vui lòng thanh toán để tiếp tục.");
    } catch (error) {
      setError(
        error.response?.data?.message ||
        (error.response?.data?.details ? `Lỗi khi tạo giao dịch CoinPayments: ${JSON.stringify(error.response.data.details)}` : "Lỗi khi tạo giao dịch CoinPayments! Vui lòng thử lại.")
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-gray-700 font-medium mb-2">Chọn loại tiền điện tử</label>
        <select
          value={selectedCurrency}
          onChange={(e) => setSelectedCurrency(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="BTC">Bitcoin (BTC)</option>
          <option value="ETH">Ethereum (ETH)</option>
          <option value="USDT">Tether (USDT)</option>
          <option value="LTC">Litecoin (LTC)</option>
          <option value="LTCT">Litecoin Testnet (LTCT)</option>
        </select>
      </div>
      {error && <p className="text-red-500">{error}</p>}
      {!transaction ? (
        <button
          onClick={handleCoinpaymentsSubmit}
          disabled={processing}
          className="w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition duration-200 disabled:bg-gray-400"
        >
          {processing ? "Đang xử lý..." : "Thanh toán qua CoinPayments"}
        </button>
      ) : (
        <div className="text-center">
          <p className="text-gray-700 mb-2">Quét mã QR để thanh toán:</p>
          <p className="text-gray-700 mb-2">Số tiền: {transaction.amount} {selectedCurrency}</p>
          <p className="text-gray-700 mb-2">Địa chỉ ví: {transaction.address}</p>
          <QRCodeCanvas value={`${selectedCurrency.toLowerCase()}:${transaction.address}?amount=${transaction.amount}`} className="mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Giao dịch sẽ được xác nhận tự động. Vui lòng chờ.</p>
        </div>
      )}
    </div>
  );
};

// Component xử lý thanh toán Coinbase Commerce
const CoinbaseCheckoutForm = ({ plan, onPending, isCoinPurchase = false, packageId, fetchWithRetry }) => {
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [charge, setCharge] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState("BTC");

  const handleCoinbaseSubmit = async () => {
    setError(null);
    setProcessing(true);

    try {
      const amount = (isCoinPurchase ? plan.price : plan.totalPrice) / 23000;
      if (!amount || isNaN(amount)) {
        throw new Error("Số tiền không hợp lệ! Vui lòng kiểm tra gói.");
      }

      const paymentData = {
        amount: amount.toFixed(2),
        currency: selectedCurrency,
        ...(isCoinPurchase ? { packageId: plan._id } : { planId: plan._id }),
      };

      const response = await fetchWithRetry({
        method: "post",
        url: isCoinPurchase ? "/api/payments/create-coin-coinbase-charge" : "/api/payments/create-membership-coinbase-charge",
        data: paymentData,
      });

      const chargeData = response.data.charge;
      setCharge(chargeData);
      setError(null);
      onPending(response.data.message || "Giao dịch đã được tạo. Vui lòng thanh toán để tiếp tục.");
    } catch (error) {
      setError(
        error.response?.data?.message ||
        (error.response?.data?.details ? `Lỗi khi tạo giao dịch Coinbase: ${JSON.stringify(error.response.data.details)}` : "Lỗi khi tạo giao dịch Coinbase Commerce! Vui lòng thử lại.")
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-gray-700 font-medium mb-2">Chọn loại tiền điện tử</label>
        <select
          value={selectedCurrency}
          onChange={(e) => setSelectedCurrency(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="BTC">Bitcoin (BTC)</option>
          <option value="ETH">Ethereum (ETH)</option>
          <option value="USDC">USD Coin (USDC)</option>
          <option value="LTC">Litecoin (LTC)</option>
          <option value="DAI">DAI</option>
        </select>
      </div>
      {error && <p className="text-red-500">{error}</p>}
      {!charge ? (
        <button
          onClick={handleCoinbaseSubmit}
          disabled={processing}
          className="w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition duration-200 disabled:bg-gray-400"
        >
          {processing ? "Đang xử lý..." : "Thanh toán qua Coinbase Commerce"}
        </button>
      ) : (
        <div className="text-center">
          <p className="text-gray-700 mb-2">Truy cập liên kết để thanh toán:</p>
          <a href={charge.hosted_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
            Thanh toán qua Coinbase Commerce
          </a>
          <p className="text-gray-500 text-sm mt-2">Giao dịch sẽ được xác nhận tự động. Vui lòng chờ.</p>
        </div>
      )}
    </div>
  );
};

// Component xử lý thanh toán NOWPayments
const NowpaymentsCheckoutForm = ({ plan, onPending, isCoinPurchase = false, packageId, fetchWithRetry }) => {
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [payment, setPayment] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState("BTC");

  const handleNowpaymentsSubmit = async () => {
    setError(null);
    setProcessing(true);

    try {
      const amount = (isCoinPurchase ? plan.price : plan.totalPrice) / 23000;
      if (!amount || isNaN(amount)) {
        throw new Error("Số tiền không hợp lệ! Vui lòng kiểm tra gói.");
      }

      const paymentData = {
        amount: amount.toFixed(2),
        currency: selectedCurrency,
        ...(isCoinPurchase ? { packageId: plan._id } : { planId: plan._id }),
      };

      const response = await fetchWithRetry({
        method: "post",
        url: isCoinPurchase ? "/api/payments/create-coin-nowpayments-payment" : "/api/payments/create-membership-nowpayments-payment",
        data: paymentData,
      });

      const paymentDataResponse = response.data.payment;
      setPayment(paymentDataResponse);
      setError(null);
      onPending(response.data.message || "Giao dịch đã được tạo. Vui lòng thanh toán để tiếp tục.");
    } catch (error) {
      setError(
        error.response?.data?.message ||
        (error.response?.data?.details ? `Lỗi khi tạo giao dịch NOWPayments: ${JSON.stringify(error.response.data.details)}` : "Lỗi khi tạo giao dịch NOWPayments! Vui lòng thử lại.")
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-gray-700 font-medium mb-2">Chọn loại tiền điện tử</label>
        <select
          value={selectedCurrency}
          onChange={(e) => setSelectedCurrency(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="BTC">Bitcoin (BTC)</option>
          <option value="ETH">Ethereum (ETH)</option>
          <option value="USDT">Tether (USDT)</option>
          <option value="LTC">Litecoin (LTC)</option>
          <option value="XRP">Ripple (XRP)</option>
        </select>
      </div>
      {error && <p className="text-red-500">{error}</p>}
      {!payment ? (
        <button
          onClick={handleNowpaymentsSubmit}
          disabled={processing}
          className="w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition duration-200 disabled:bg-gray-400"
        >
          {processing ? "Đang xử lý..." : "Thanh toán qua NOWPayments"}
        </button>
      ) : (
        <div className="text-center">
          <p className="text-gray-700 mb-2">Quét mã QR để thanh toán:</p>
          <p className="text-gray-700 mb-2">Số tiền: {payment.pay_amount} {selectedCurrency}</p>
          <p className="text-gray-700 mb-2">Địa chỉ ví: {payment.pay_address}</p>
          <QRCodeCanvas value={`${selectedCurrency.toLowerCase()}:${payment.pay_address}?amount=${payment.pay_amount}`} className="mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Giao dịch sẽ được xác nhận tự động. Vui lòng chờ.</p>
        </div>
      )}
    </div>
  );
};

// Component xử lý thanh toán Binance Pay
const BinancepayCheckoutForm = ({ plan, onPending, isCoinPurchase = false, packageId, fetchWithRetry }) => {
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [order, setOrder] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        setLoadingCurrencies(true);
        const response = await fetchWithRetry({
          method: "get",
          url: "/api/payments/binancepay-supported-currencies",
        });
        const supportedCurrencies = response.data;
        setCurrencies(supportedCurrencies);
        if (supportedCurrencies.length > 0) {
          setSelectedCurrency(supportedCurrencies[0].currency);
        }
      } catch (error) {
        setError(
          error.response?.data?.message ||
          (error.response?.data?.details ? `Lỗi khi tải danh sách coin: ${JSON.stringify(error.response.data.details)}` : "Không thể tải danh sách coin hỗ trợ từ Binance Pay! Vui lòng thử lại.")
        );
      } finally {
        setLoadingCurrencies(false);
      }
    };
    fetchCurrencies();
  }, [fetchWithRetry]);

  const handleBinancepaySubmit = async () => {
    setError(null);
    setProcessing(true);

    try {
      const amount = (isCoinPurchase ? plan.price : plan.totalPrice) / 23000;
      if (!amount || isNaN(amount)) {
        throw new Error("Số tiền không hợp lệ! Vui lòng kiểm tra gói.");
      }

      const paymentData = {
        amount: amount.toFixed(2),
        currency: selectedCurrency,
        ...(isCoinPurchase ? { packageId: plan._id } : { planId: plan._id }),
      };

      const response = await fetchWithRetry({
        method: "post",
        url: isCoinPurchase ? "/api/payments/create-coin-binancepay-order" : "/api/payments/create-membership-binancepay-order",
        data: paymentData,
      });

      const orderData = response.data.order;
      setOrder(orderData);
      setError(null);
      onPending(response.data.message || "Giao dịch đã được tạo. Vui lòng thanh toán để tiếp tục.");
    } catch (error) {
      setError(
        error.response?.data?.message ||
        (error.response?.data?.details ? `Lỗi khi tạo giao dịch Binance Pay: ${JSON.stringify(error.response.data.details)}` : "Lỗi khi tạo giao dịch Binance Pay! Vui lòng thử lại.")
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-gray-700 font-medium mb-2">Chọn loại tiền điện tử</label>
        {loadingCurrencies ? (
          <p className="text-gray-500">Đang tải danh sách coin...</p>
        ) : currencies.length > 0 ? (
          <select
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {currencies.map((currency) => (
              <option key={currency.currency} value={currency.currency}>
                {currency.currency} ({currency.name})
              </option>
            ))}
          </select>
        ) : (
          <p className="text-red-500">Không có coin nào được hỗ trợ!</p>
        )}
      </div>
      {error && <p className="text-red-500">{error}</p>}
      {!order ? (
        <button
          onClick={handleBinancepaySubmit}
          disabled={loadingCurrencies || currencies.length === 0 || processing}
          className="w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition duration-200 disabled:bg-gray-400"
        >
          {processing ? "Đang xử lý..." : "Thanh toán qua Binance Pay"}
        </button>
      ) : (
        <div className="text-center">
          <p className="text-gray-700 mb-2">Quét mã QR hoặc truy cập liên kết để thanh toán:</p>
          <a href={order.checkoutUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
            Thanh toán qua Binance Pay
          </a>
          <p className="text-gray-700 mb-2">Số tiền: {order.orderAmount} USD</p>
          <QRCodeCanvas value={order.checkoutUrl} className="mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Giao dịch sẽ được xác nhận tự động. Vui lòng chờ.</p>
        </div>
      )}
    </div>
  );
};

// Component xử lý thanh toán MoMo
const MomoCheckoutForm = ({ plan, onPending, isCoinPurchase = false, packageId, fetchWithRetry }) => {
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [payUrl, setPayUrl] = useState(null);

  const handleMoMoSubmit = async () => {
    setError(null);
    setProcessing(true);

    try {
      const amount = isCoinPurchase ? plan.price : plan.totalPrice;
      if (!amount || isNaN(amount)) {
        throw new Error("Số tiền không hợp lệ! Vui lòng kiểm tra gói.");
      }

      const paymentData = {
        amount,
        ...(isCoinPurchase ? { packageId: plan._id } : { planId: plan._id }),
      };

      const response = await fetchWithRetry({
        method: "post",
        url: isCoinPurchase ? "/api/local-payments/create-coin-momo-payment" : "/api/local-payments/create-membership-momo-payment",
        data: paymentData,
      });

      setPayUrl(response.data.payUrl);
      setError(null);
      onPending(response.data.message || "Giao dịch đã được tạo. Vui lòng thanh toán để tiếp tục.");
    } catch (error) {
      setError(
        error.response?.data?.message ||
        (error.response?.data?.details ? `Lỗi khi tạo giao dịch MoMo: ${JSON.stringify(error.response.data.details)}` : "Lỗi khi tạo giao dịch MoMo! Vui lòng thử lại.")
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && <p className="text-red-500">{error}</p>}
      {!payUrl ? (
        <button
          onClick={handleMoMoSubmit}
          disabled={processing}
          className="w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition duration-200 disabled:bg-gray-400"
        >
          {processing ? "Đang xử lý..." : "Thanh toán qua MoMo"}
        </button>
      ) : (
        <div className="text-center">
          <p className="text-gray-700 mb-2">Quét mã QR hoặc truy cập liên kết để thanh toán:</p>
          <a href={payUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
            Thanh toán qua MoMo
          </a>
          <p className="text-gray-700 mb-2">Số tiền: {(isCoinPurchase ? plan.price : plan.totalPrice).toLocaleString()} VND</p>
          <QRCodeCanvas value={payUrl} className="mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Giao dịch sẽ được xác nhận tự động. Vui lòng chờ.</p>
        </div>
      )}
    </div>
  );
};

// Component xử lý thanh toán ZaloPay
const ZaloPayCheckoutForm = ({ plan, onPending, isCoinPurchase = false, packageId, fetchWithRetry }) => {
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [payUrl, setPayUrl] = useState(null);

  const handleZaloPaySubmit = async () => {
    setError(null);
    setProcessing(true);

    try {
      const amount = isCoinPurchase ? plan.price : plan.totalPrice;
      if (!amount || isNaN(amount)) {
        throw new Error("Số tiền không hợp lệ! Vui lòng kiểm tra gói.");
      }

      const paymentData = {
        amount,
        ...(isCoinPurchase ? { packageId: plan._id } : { planId: plan._id }),
      };

      const response = await fetchWithRetry({
        method: "post",
        url: isCoinPurchase ? "/api/local-payments/create-coin-zalopay-payment" : "/api/local-payments/create-membership-zalopay-payment",
        data: paymentData,
      });

      setPayUrl(response.data.payUrl);
      setError(null);
      onPending(response.data.message || "Giao dịch đã được tạo. Vui lòng thanh toán để tiếp tục.");
    } catch (error) {
      setError(
        error.response?.data?.message ||
        (error.response?.data?.details ? `Lỗi khi tạo giao dịch ZaloPay: ${JSON.stringify(error.response.data.details)}` : "Lỗi khi tạo giao dịch ZaloPay! Vui lòng thử lại.")
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && <p className="text-red-500">{error}</p>}
      {!payUrl ? (
        <button
          onClick={handleZaloPaySubmit}
          disabled={processing}
          className="w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition duration-200 disabled:bg-gray-400"
        >
          {processing ? "Đang xử lý..." : "Thanh toán qua ZaloPay"}
        </button>
      ) : (
        <div className="text-center">
          <p className="text-gray-700 mb-2">Quét mã QR hoặc truy cập liên kết để thanh toán:</p>
          <a href={payUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
            Thanh toán qua ZaloPay
          </a>
          <p className="text-gray-700 mb-2">Số tiền: {(isCoinPurchase ? plan.price : plan.totalPrice).toLocaleString()} VND</p>
          <QRCodeCanvas value={payUrl} className="mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Giao dịch sẽ được xác nhận tự động. Vui lòng chờ.</p>
        </div>
      )}
    </div>
  );
};

// Component xử lý thanh toán VNPay
const VNPayCheckoutForm = ({ plan, onPending, isCoinPurchase = false, packageId, fetchWithRetry }) => {
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [payUrl, setPayUrl] = useState(null);

  const handleVNPaySubmit = async () => {
    setError(null);
    setProcessing(true);

    try {
      const amount = isCoinPurchase ? plan.price : plan.totalPrice;
      if (!amount || isNaN(amount)) {
        throw new Error("Số tiền không hợp lệ! Vui lòng kiểm tra gói.");
      }

      const paymentData = {
        amount,
        ...(isCoinPurchase ? { packageId: plan._id } : { planId: plan._id }),
      };

      const response = await fetchWithRetry({
        method: "post",
        url: isCoinPurchase ? "/api/local-payments/create-coin-vnpay-payment" : "/api/local-payments/create-membership-vnpay-payment",
        data: paymentData,
      });

      setPayUrl(response.data.payUrl);
      setError(null);
      onPending(response.data.message || "Giao dịch đã được tạo. Vui lòng thanh toán để tiếp tục.");
    } catch (error) {
      setError(
        error.response?.data?.message ||
        (error.response?.data?.details ? `Lỗi khi tạo giao dịch VNPay: ${JSON.stringify(error.response.data.details)}` : "Lỗi khi tạo giao dịch VNPay! Vui lòng thử lại.")
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && <p className="text-red-500">{error}</p>}
      {!payUrl ? (
        <button
          onClick={handleVNPaySubmit}
          disabled={processing}
          className="w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition duration-200 disabled:bg-gray-400"
        >
          {processing ? "Đang xử lý..." : "Thanh toán qua VNPay"}
        </button>
      ) : (
        <div className="text-center">
          <p className="text-gray-700 mb-2">Quét mã QR hoặc truy cập liên kết để thanh toán:</p>
          <a href={payUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
            Thanh toán qua VNPay
          </a>
          <p className="text-gray-700 mb-2">Số tiền: {(isCoinPurchase ? plan.price : plan.totalPrice).toLocaleString()} VND</p>
          <QRCodeCanvas value={payUrl} className="mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Giao dịch sẽ được xác nhận tự động. Vui lòng chờ.</p>
        </div>
      )}
    </div>
  );
};

// Component xử lý thanh toán VietQR
const VietQRCheckoutForm = ({ plan, onPending, isCoinPurchase = false, packageId, fetchWithRetry, user }) => {
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const handleVietQRSubmit = async () => {
    setError(null);
    setSuccessMessage(null);
    setIsConfirmed(false);
    setProcessing(true);

    try {
      const amount = isCoinPurchase ? plan.price : plan.totalPrice;
      if (!amount || isNaN(amount)) {
        throw new Error("Số tiền không hợp lệ! Vui lòng kiểm tra gói.");
      }

      const paymentData = {
        amount,
        ...(isCoinPurchase ? { packageId: plan._id } : { planId: plan._id }),
      };

      const response = await fetchWithRetry({
        method: "post",
        url: isCoinPurchase ? "/api/local-payments/create-coin-vietqr-payment" : "/api/local-payments/create-membership-vietqr-payment",
        data: paymentData,
      });

      setQrData(response.data.qrData);
      setTransactionId(response.data.vietqrTransactionId);
      setError(null);
      onPending(response.data.message || "Giao dịch đã được tạo. Vui lòng thanh toán để tiếp tục.");
    } catch (error) {
      setError(
        error.response?.data?.message ||
        (error.response?.data?.details ? `Lỗi khi tạo mã VietQR: ${JSON.stringify(error.response.data.details)}` : "Lỗi khi tạo mã VietQR! Vui lòng thử lại.")
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmPayment = async () => {
    setError(null);
    setSuccessMessage(null);
    setProcessing(true);

    // Kiểm tra xem transactionId có hợp lệ không
    if (!transactionId) {
      setError("Không thể xác định mã giao dịch! Vui lòng thử lại.");
      setProcessing(false);
      return;
    }

    try {
      const response = await fetchWithRetry({
        method: "post",
        url: isCoinPurchase ? "/api/local-payments/confirm-coin-vietqr-payment" : "/api/local-payments/confirm-membership-vietqr-payment",
        data: { vietqrTransactionId: transactionId }, // Chỉ gửi vietqrTransactionId
      });
      setSuccessMessage(response.data.message || "Giao dịch đã được ghi nhận và đang chờ xác minh.");
      setIsConfirmed(true);
    } catch (error) {
      setError(
        error.response?.data?.message ||
        (error.response?.data?.details ? `Lỗi khi xác nhận giao dịch: ${JSON.stringify(error.response.data.details)}` : "Lỗi khi xác nhận giao dịch! Vui lòng thử lại.")
      );
    } finally {
      setProcessing(false);
    }
  };

  // Hiển thị transactionId đầy đủ trong giao diện
  const displayTransactionCode = transactionId || (user && user._id ? user._id : null);

  // Kiểm tra displayTransactionCode ngay khi giao diện được render
  if (!displayTransactionCode && !qrData) {
    setError("Không thể xác định mã giao dịch! Vui lòng thử lại hoặc liên hệ hỗ trợ.");
  }

  return (
    <div className="space-y-4">
      {error && <p className="text-red-500">{error}</p>}
      {successMessage && <p className="text-green-500">{successMessage}</p>}
      {!qrData ? (
        <button
          onClick={handleVietQRSubmit}
          disabled={processing || !user || !user._id}
          className="w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition duration-200 disabled:bg-gray-400"
        >
          {processing ? "Đang xử lý..." : "Thanh toán qua VietQR"}
        </button>
      ) : (
        <div className="text-center">
          <p className="text-gray-700 mb-2">Quét mã QR để thanh toán:</p>
          <p className="text-gray-700 mb-2">Số tiền: {(isCoinPurchase ? plan.price : plan.totalPrice).toLocaleString()} VND</p>
          <QRCodeCanvas value={qrData} className="mx-auto mb-4" />
          <p className="text-gray-500 text-sm mb-4">Sau khi thanh toán, nhấn nút xác nhận.</p>
          {displayTransactionCode && (
            <p className="text-gray-500 text-sm mb-4">Mã giao dịch: {displayTransactionCode}. Lưu mã này để xác minh nếu cần.</p>
          )}
          {!isConfirmed && (
            <button
              onClick={handleConfirmPayment}
              disabled={processing || !displayTransactionCode}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition duration-200 disabled:bg-gray-400"
            >
              {processing ? "Đang xử lý..." : "Xác nhận đã thanh toán"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
// Component chính
const PaymentForms = ({
  plan,
  packageId,
  isCoinPurchase = false,
  onSuccess,
  onPending,
  paymentMethod,
  setPaymentMethod,
  paymentGateways,
  localPaymentGateways,
  fetchWithRetry,
  user,
  stripePromise,
}) => {
  return (
    <div className="bg-white p-6 rounded-xl shadow-lg">
      <div className="mb-4">
        <label className="block text-gray-700 font-medium mb-2">Chọn cổng thanh toán</label>
        <div className="flex space-x-4 justify-center flex-wrap">
          {paymentGateways.stripe?.enabled && (
            <div
              onClick={() => setPaymentMethod("stripe")}
              className={`cursor-pointer p-2 rounded-lg border-2 transition-all duration-200 ${
                paymentMethod === "stripe" ? "border-blue-500 bg-blue-50" : "border-gray-300"
              }`}
            >
              <img src={stripeLogo} alt="Stripe" className="h-12 w-auto object-contain" />
            </div>
          )}
          {paymentGateways.paypal_api?.enabled && paymentGateways.paypal_api?.clientId && (
            <div
              onClick={() => setPaymentMethod("paypal_api")}
              className={`cursor-pointer p-2 rounded-lg border-2 transition-all duration-200 ${
                paymentMethod === "paypal_api" ? "border-blue-500 bg-blue-50" : "border-gray-300"
              }`}
            >
              <img src={paypalLogo} alt="PayPal API" className="h-12 w-auto object-contain" />
            </div>
          )}
          {paymentGateways.paypal_me?.enabled && paymentGateways.paypal_me?.username && (
            <div
              onClick={() => setPaymentMethod("paypal_me")}
              className={`cursor-pointer p-2 rounded-lg border-2 transition-all duration-200 ${
                paymentMethod === "paypal_me" ? "border-blue-500 bg-blue-50" : "border-gray-300"
              }`}
            >
              <img src={paypalLogo} alt="PayPal.me" className="h-12 w-auto object-contain" />
            </div>
          )}
          {paymentGateways.coinpayments?.enabled && paymentGateways.coinpayments?.merchantId && (
            <div
              onClick={() => setPaymentMethod("coinpayments")}
              className={`cursor-pointer p-2 rounded-lg border-2 transition-all duration-200 ${
                paymentMethod === "coinpayments" ? "border-blue-500 bg-blue-50" : "border-gray-300"
              }`}
            >
              <img src={coinpaymentsLogo} alt="CoinPayments" className="h-12 w-auto object-contain" />
            </div>
          )}
          {paymentGateways.coinbase?.enabled && (
            <div
              onClick={() => setPaymentMethod("coinbase")}
              className={`cursor-pointer p-2 rounded-lg border-2 transition-all duration-200 ${
                paymentMethod === "coinbase" ? "border-blue-500 bg-blue-50" : "border-gray-300"
              }`}
            >
              <img src={coinbaseLogo} alt="Coinbase Commerce" className="h-12 w-auto object-contain" />
            </div>
          )}
          {paymentGateways.nowpayments?.enabled && (
            <div
              onClick={() => setPaymentMethod("nowpayments")}
              className={`cursor-pointer p-2 rounded-lg border-2 transition-all duration-200 ${
                paymentMethod === "nowpayments" ? "border-blue-500 bg-blue-50" : "border-gray-300"
              }`}
            >
              <img src={nowpaymentsLogo} alt="NOWPayments" className="h-12 w-auto object-contain" />
            </div>
          )}
          {paymentGateways.binancepay?.enabled && (
            <div
              onClick={() => setPaymentMethod("binancepay")}
              className={`cursor-pointer p-2 rounded-lg border-2 transition-all duration-200 ${
                paymentMethod === "binancepay" ? "border-blue-500 bg-blue-50" : "border-gray-300"
              }`}
            >
              <img src={binancepayLogo} alt="Binance Pay" className="h-12 w-auto object-contain" />
            </div>
          )}
          {localPaymentGateways.momo?.enabled && (
            <div
              onClick={() => setPaymentMethod("momo")}
              className={`cursor-pointer p-2 rounded-lg border-2 transition-all duration-200 ${
                paymentMethod === "momo" ? "border-blue-500 bg-blue-50" : "border-gray-300"
              }`}
            >
              <img src={momoLogo} alt="MoMo" className="h-12 w-auto object-contain" />
            </div>
          )}
          {localPaymentGateways.zalopay?.enabled && (
            <div
              onClick={() => setPaymentMethod("zalopay")}
              className={`cursor-pointer p-2 rounded-lg border-2 transition-all duration-200 ${
                paymentMethod === "zalopay" ? "border-blue-500 bg-blue-50" : "border-gray-300"
              }`}
            >
              <img src={zalopayLogo} alt="ZaloPay" className="h-12 w-auto object-contain" />
            </div>
          )}
          {localPaymentGateways.vnpay?.enabled && (
            <div
              onClick={() => setPaymentMethod("vnpay")}
              className={`cursor-pointer p-2 rounded-lg border-2 transition-all duration-200 ${
                paymentMethod === "vnpay" ? "border-blue-500 bg-blue-50" : "border-gray-300"
              }`}
            >
              <img src={vnpayLogo} alt="VNPay" className="h-12 w-auto object-contain" />
            </div>
          )}
          {localPaymentGateways.vietqr?.enabled && (
            <div
              onClick={() => setPaymentMethod("vietqr")}
              className={`cursor-pointer p-2 rounded-lg border-2 transition-all duration-200 ${
                paymentMethod === "vietqr" ? "border-blue-500 bg-blue-50" : "border-gray-300"
              }`}
            >
              <img src={vietqrLogo} alt="VietQR" className="h-12 w-auto object-contain" />
            </div>
          )}
        </div>
        {(paymentMethod === "paypal_api" ||
          paymentMethod === "paypal_me" ||
          paymentMethod === "coinpayments" ||
          paymentMethod === "coinbase" ||
          paymentMethod === "nowpayments" ||
          paymentMethod === "binancepay") && (
          <p className="text-sm text-gray-500 mt-2 text-center">Lưu ý: Số tiền sẽ được quy đổi sang USD (1 USD ≈ 23,000 VND).</p>
        )}
        {paymentMethod && (
          <p className="text-gray-700 text-center mt-2">
            {paymentMethod === "stripe"
              ? "Thanh toán qua Stripe"
              : paymentMethod === "paypal_api"
              ? "Thanh toán qua PayPal API"
              : paymentMethod === "paypal_me"
              ? `Thanh toán qua PayPal.me (Username: ${paymentGateways.paypal_me?.username})`
              : paymentMethod === "coinpayments"
              ? "Thanh toán qua CoinPayments (Tiền điện tử)"
              : paymentMethod === "coinbase"
              ? "Thanh toán qua Coinbase Commerce (Tiền điện tử)"
              : paymentMethod === "nowpayments"
              ? "Thanh toán qua NOWPayments (Tiền điện tử)"
              : paymentMethod === "binancepay"
              ? "Thanh toán qua Binance Pay (Tiền điện tử)"
              : paymentMethod === "momo"
              ? "Thanh toán qua MoMo"
              : paymentMethod === "zalopay"
              ? "Thanh toán qua ZaloPay"
              : paymentMethod === "vnpay"
              ? "Thanh toán qua VNPay"
              : paymentMethod === "vietqr"
              ? "Thanh toán qua VietQR"
              : ""}
          </p>
        )}
      </div>
      {paymentMethod ? (
        paymentMethod === "stripe" && stripePromise ? (
          <Elements stripe={stripePromise}>
            <StripeCheckoutForm plan={plan} packageId={packageId} isCoinPurchase={isCoinPurchase} onSuccess={onSuccess} fetchWithRetry={fetchWithRetry} />
          </Elements>
        ) : paymentMethod === "paypal_api" && paymentGateways.paypal_api?.clientId ? (
          <PaypalApiCheckoutForm
            plan={plan}
            packageId={packageId}
            isCoinPurchase={isCoinPurchase}
            clientId={paymentGateways.paypal_api.clientId}
            onSuccess={onSuccess}
            fetchWithRetry={fetchWithRetry}
          />
        ) : paymentMethod === "paypal_me" && paymentGateways.paypal_me?.username ? (
          <PaypalMeCheckoutForm
            plan={plan}
            packageId={packageId}
            isCoinPurchase={isCoinPurchase}
            username={paymentGateways.paypal_me.username}
            onPending={onPending}
            fetchWithRetry={fetchWithRetry}
          />
        ) : paymentMethod === "coinpayments" && paymentGateways.coinpayments?.merchantId ? (
          <CoinpaymentsCheckoutForm
            plan={plan}
            packageId={packageId}
            isCoinPurchase={isCoinPurchase}
            merchantId={paymentGateways.coinpayments.merchantId}
            onPending={onPending}
            fetchWithRetry={fetchWithRetry}
          />
        ) : paymentMethod === "coinbase" && paymentGateways.coinbase?.enabled ? (
          <CoinbaseCheckoutForm
            plan={plan}
            packageId={packageId}
            isCoinPurchase={isCoinPurchase}
            onPending={onPending}
            fetchWithRetry={fetchWithRetry}
          />
        ) : paymentMethod === "nowpayments" && paymentGateways.nowpayments?.enabled ? (
          <NowpaymentsCheckoutForm
            plan={plan}
            packageId={packageId}
            isCoinPurchase={isCoinPurchase}
            onPending={onPending}
            fetchWithRetry={fetchWithRetry}
          />
        ) : paymentMethod === "binancepay" && paymentGateways.binancepay?.enabled ? (
          <BinancepayCheckoutForm
            plan={plan}
            packageId={packageId}
            isCoinPurchase={isCoinPurchase}
            onPending={onPending}
            fetchWithRetry={fetchWithRetry}
          />
        ) : paymentMethod === "momo" && localPaymentGateways.momo?.enabled ? (
          <MomoCheckoutForm
            plan={plan}
            packageId={packageId}
            isCoinPurchase={isCoinPurchase}
            onPending={onPending}
            fetchWithRetry={fetchWithRetry}
          />
        ) : paymentMethod === "zalopay" && localPaymentGateways.zalopay?.enabled ? (
          <ZaloPayCheckoutForm
            plan={plan}
            packageId={packageId}
            isCoinPurchase={isCoinPurchase}
            onPending={onPending}
            fetchWithRetry={fetchWithRetry}
          />
        ) : paymentMethod === "vnpay" && localPaymentGateways.vnpay?.enabled ? (
          <VNPayCheckoutForm
            plan={plan}
            packageId={packageId}
            isCoinPurchase={isCoinPurchase}
            onPending={onPending}
            fetchWithRetry={fetchWithRetry}
          />
        ) : paymentMethod === "vietqr" && localPaymentGateways.vietqr?.enabled ? (
          <VietQRCheckoutForm
            plan={plan}
            packageId={packageId}
            isCoinPurchase={isCoinPurchase}
            onPending={onPending}
            fetchWithRetry={fetchWithRetry}
            user={user}
          />
        ) : (
          <p className="text-red-500 text-center">Cổng thanh toán không khả dụng! Vui lòng kiểm tra cấu hình.</p>
        )
      ) : (
        <p className="text-red-500 text-center">Vui lòng chọn cổng thanh toán!</p>
      )}
    </div>
  );
};

export default PaymentForms;